// src/pages/Payment.js
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { FaCcStripe, FaWallet, FaLock, FaUniversity } from 'react-icons/fa'; // Payment icons
import { useCart } from '../context/CartContext'; // To clear cart on success
import { useSettings } from '../context/SettingsContext'; // To get bank details
import UnifiedStripeModal from '../components/UnifiedStripeModal'; // Stripe Elements Modal
import SuccessModal from '../components/SuccessModal'; // Assumes this component exists
import CancelModal from '../components/CancelModal';   // Assumes this component exists
// Optional: Import Bootstrap components if needed beyond the modal
// import { Alert } from 'react-bootstrap';

function Payment() {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const { settings } = useSettings();
  const [paymentError, setPaymentError] = useState('');
  // State to track loading state for redirect buttons
  const [isProcessing, setIsProcessing] = useState({
      stripe: false
      // wallet modal handles its own internal processing state via 'processing' prop
  });
  // State to control modal visibility
  const [modals, setModals] = useState({
    success: false,
    cancel: false,
    wallet: false, // For the UnifiedStripeModal
    bankTransfer: false // For bank transfer details modal
  });

  // State to hold booking/cart data retrieved from localStorage
  const [paymentData, setPaymentData] = useState(() => {
      const defaultData = { totalPrice: 0, bookingData: null, cartItems: [], isValid: false };
      try {
        const storedBooking = localStorage.getItem('bookingInfo');
        if (!storedBooking) {
            console.warn("Payment page: No bookingInfo found in localStorage.");
            return defaultData;
        }
        const parsedData = JSON.parse(storedBooking);

        // Validate the structure and content more thoroughly
        const bookingValid = !!(parsedData.bookingData?.name && parsedData.bookingData?.email && parsedData.bookingData?.address);
        const itemsValid = Array.isArray(parsedData.cartItems) && parsedData.cartItems.length > 0 && parsedData.cartItems.every(item => item.name && typeof item.cost === 'number' && item.cost >= 0);
        const priceValid = typeof parsedData.totalPrice === 'number' && parsedData.totalPrice > 0; // Allow 0 price? Maybe not for payment page.

        const isValid = bookingValid && itemsValid && priceValid;

        if (!isValid) {
            console.error("Payment page: Stored bookingInfo is invalid or incomplete.", { bookingValid, itemsValid, priceValid, parsedData });
            // Optionally clear invalid data here: localStorage.removeItem('bookingInfo');
        }

        // Ensure totalPrice is a number, default to 0 if parsing fails or invalid
        parsedData.totalPrice = Number(parsedData.totalPrice) || 0;
        // Ensure cartItems is an array
        parsedData.cartItems = Array.isArray(parsedData.cartItems) ? parsedData.cartItems : [];

        return { ...parsedData, isValid };
      } catch (error) {
          console.error('Payment data initialization error:', error);
          // Clear potentially corrupted data
          localStorage.removeItem('bookingInfo');
          return defaultData;
      }
  });

  // =====================================================
  // CALLBACK DEFINITIONS - ORDER MATTERS FOR DEPENDENCIES
  // =====================================================

  // Utility to clear stored booking data (no dependencies)
  const clearLocalStorage = useCallback(() => {
    console.log("Clearing bookingInfo from localStorage.");
    localStorage.removeItem('bookingInfo');
    // Also clear related form data if applicable
    // localStorage.removeItem('bookingForm');
  }, []);

  // Send Confirmation Email function (depends on paymentData)
  const sendConfirmationEmail = useCallback(async () => {
    if (!paymentData.isValid) {
      console.error("Send Email: Cannot send, invalid paymentData.");
      throw new Error('Cannot send confirmation: Booking data is invalid.');
    }
    console.log("Send Email: Sending confirmation request...");
    try {
        const response = await fetch('/api/send-confirmation-email', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ // Ensure structure matches backend expectation
                bookingData: paymentData.bookingData,
                cartItems: paymentData.cartItems,
                totalPrice: paymentData.totalPrice
            })
        });
        if (!response.ok) {
             let errorMsg = 'Email server responded with an error.';
             try { const eData = await response.json(); errorMsg = eData.error || errorMsg; } catch (_) { /* ignore */ }
             console.error(`Send Email: Server error - Status ${response.status}`);
             throw new Error(errorMsg);
        }
        console.log('Confirmation email request successfully processed by backend.');
        // Don't need to parse success response unless necessary
    } catch (error) {
        console.error('Send Email: Fetch/Network error:', error);
        throw error; // Re-throw to be caught by handlePaymentSuccess
    }
  }, [paymentData]); // Depends only on paymentData


  // Common actions after any successful payment (depends on clearCart, clearLocalStorage)
  const handlePaymentSuccessActions = useCallback(() => {
      console.log("Payment Success Actions: Clearing cart and local storage.");
      clearCart(); // Clear the application's cart state
      clearLocalStorage(); // Clear stored booking info
      // Close any open payment modals and show success modal
      setModals(prev => ({ ...prev, wallet: false, success: true, cancel: false }));
      setIsProcessing({ stripe: false }); // Reset loading states
  }, [clearCart, clearLocalStorage]);


  // Callback triggered by payment success (depends on paymentData, sendConfirmationEmail, handlePaymentSuccessActions)
  const handlePaymentSuccess = useCallback(async (isFromRedirect = false) => {
        console.log(`Handling Payment Success (From Redirect: ${isFromRedirect})`);
        setIsProcessing({ stripe: false }); // Ensure loading stops

        // Double-check data validity before proceeding
        if (!paymentData.isValid) {
            console.error("Payment Success Handler: Invalid paymentData state.");
            setPaymentError('Payment completed, but booking data was missing or invalid. Please contact support with your payment details.');
            clearLocalStorage(); // Clear potentially bad data
             // Don't show standard success modal if data is bad
             setModals(prev => ({ ...prev, wallet: false, success: false, cancel: false }));
            return;
        }

        try {
            console.log("Payment Success Handler: Attempting to send confirmation email.");
            // Now sendConfirmationEmail is guaranteed to be defined here
            await sendConfirmationEmail();
            handlePaymentSuccessActions(); // Clear data, show success modal
        } catch (emailError) {
            // Although sendConfirmationEmail handles internal errors, catch re-throws if any
            console.error("Payment Success Handler: Email function threw error:", emailError);
             setPaymentError(`Payment succeeded but the confirmation email system failed: ${emailError.message}. Your booking is confirmed.`);
             // Still proceed with success actions as payment was successful
             handlePaymentSuccessActions();
        }
  }, [paymentData, sendConfirmationEmail, handlePaymentSuccessActions, clearLocalStorage]); // Dependencies updated


  // Callback triggered by payment cancellation (depends on clearLocalStorage)
  const handlePaymentCancel = useCallback(() => {
        console.log("Handling Payment Cancel");
        setIsProcessing({ stripe: false });
        clearLocalStorage(); // Clear data so user has to restart booking
        setModals(prev => ({ ...prev, wallet: false, success: false, cancel: true })); // Show cancel modal
  }, [clearLocalStorage]);


  // --- Effect to handle status from URL query params (handles redirects) ---
  // (depends on handlePaymentSuccess, handlePaymentCancel)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const status = searchParams.get('status');
    const source = searchParams.get('source'); // e.g., 'stripe'
    // const sessionId = searchParams.get('session_id'); // Optional: For Stripe Checkout verification

    console.log(`Payment Page Load: URL status='${status}', source='${source}'`);

    let processed = false; // Basic flag to prevent quick re-processing

    if (status && !processed) {
        processed = true;
        if (status === 'success') {
            console.log('Redirect Success detected.');
            handlePaymentSuccess(true); // Indicate it came from redirect
        } else if (status === 'cancel') {
            console.log('Redirect Cancel detected.');
            handlePaymentCancel();
        }
        // Clean the URL to prevent re-triggering on refresh/back navigation
        console.log('Cleaning URL parameters.');
        navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate, handlePaymentSuccess, handlePaymentCancel]); // Effect dependencies


  // --- Specific Checkout Handlers ---

  // Stripe Checkout (Redirect Flow) Handler (depends on paymentData)
  const handleStripeCheckout = useCallback(async () => {
    if (!paymentData.isValid) { setPaymentError('Invalid booking information for Stripe Checkout.'); return; }
    setIsProcessing(prev => ({ ...prev, stripe: true }));
    setPaymentError('');
    try {
      console.log('Stripe Checkout: Requesting session with items:', paymentData.cartItems);
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cartItems: paymentData.cartItems }) // Send cart items
      });

      if (!response.ok) {
          let errorMsg = 'Stripe session creation failed.';
          try { const e = await response.json(); errorMsg = e.error || errorMsg; } catch (_) { /* ignore */ }
          console.error(`Stripe Checkout: Server error ${response.status}`);
          throw new Error(errorMsg);
      }
      const { url, error } = await response.json();
      if (error) { throw new Error(error); }
      if (!url) { throw new Error('Missing Stripe payment gateway URL'); }

      console.log('Stripe Checkout: Redirecting to Stripe...');
      window.location.href = url; // Redirect user
    } catch (error) {
        console.error('Stripe Checkout Error:', error);
        setPaymentError(error.message || 'Failed to start Stripe payment.');
        setIsProcessing(prev => ({ ...prev, stripe: false })); // Reset loading on error
    }
    // No need to reset processing state here if redirect occurs successfully
  }, [paymentData]);




  // --- Main Action Dispatcher ---
  // Decides which payment flow to initiate or which modal to open
  // (depends on paymentData.isValid, handleStripeCheckout)
  const handlePaymentAction = useCallback((processor) => {
    setPaymentError(''); // Clear previous errors
    if (!paymentData.isValid) {
        console.warn(`Payment Action (${processor}): Invalid paymentData, cannot proceed.`);
        setPaymentError('Cannot proceed: Booking information is invalid. Please return to booking.');
        return;
    }

    console.log(`Payment Action: Triggering processor '${processor}'`);
    switch (processor) {
      case 'stripe': handleStripeCheckout(); break; // Uses Checkout redirect
      case 'wallet': setModals(prev => ({ ...prev, wallet: true })); break; // Opens Elements modal
      case 'bankTransfer': setModals(prev => ({ ...prev, bankTransfer: true })); break; // Opens bank transfer modal
      default: console.error('Invalid payment processor selected:', processor); setPaymentError('Invalid payment option.');
    }
  }, [paymentData.isValid, handleStripeCheckout]);


  // =====================================================
  // RENDER LOGIC
  // =====================================================

  // Display loading or error if booking data is invalid on initial load
   if (!paymentData.isValid && !modals.success && !modals.cancel) {
       // Avoid showing payment options if data is bad
       return (
           <div className="container my-5 text-center">
               <h1 className="display-5 text-danger">Payment Error</h1>
               <p className="lead">Could not load valid booking or cart information from the previous step.</p>
               <p>Please ensure you have selected items and completed the booking details correctly.</p>
               {/* Provide a link back to the start of the booking process */}
               <Link to="/booking" className="btn btn-dark mt-3">Return to Booking Page</Link>
           </div>
       );
   }

  // Main payment page content
  return (
    <div className="container my-5">
      {/* Header Section */}
      <div className="text-center mb-5">
        <h1 className="display-4" style={styles.headerTitle}>Complete Your Booking</h1>
        <p className="lead" style={styles.headerSubtitle}>Choose how you'd like to pay for your repair.</p>
        <p style={styles.totalAmountStyle}>
            Total Amount: £{(paymentData.totalPrice || 0).toFixed(2)}
        </p>
        {/* General Error Display Area */}
        {paymentError && !modals.success && !modals.cancel && (
          <div style={styles.errorBoxStyle} role="alert">{paymentError}</div>
        )}
      </div>

      {/* Two Column Layout: Pay Now | Pay After */}
      <div className="row justify-content-center g-4 mb-5">
        {/* Pay Now Section */}
        <div className="col-md-6">
          <div style={styles.paymentSectionBox}>
            <h2 style={styles.sectionTitle}>Pay Now</h2>
            <p style={styles.sectionDescription}>Complete your payment immediately and receive confirmation.</p>
            <div className="row g-3">
              {/* Stripe Checkout Option */}
              <PaymentOption
                  icon={<FaCcStripe size={50} />}
                  title="Credit/Debit Card"
                  action={() => handlePaymentAction('stripe')}
                  isLoading={isProcessing.stripe}
                  buttonText="Pay with Card"
              />
              {/* Stripe Elements (Modal) Option */}
              <PaymentOption
                  icon={<FaWallet size={50} />}
                  title="Wallet / Secure Card"
                  action={() => handlePaymentAction('wallet')}
                  buttonText="Use Apple/Google Pay"
              />
              {/* Bank Transfer Option */}
              {settings.bankDetails && (
              <PaymentOption
                  icon={<FaUniversity size={50} />}
                  title="Bank Transfer"
                  action={() => handlePaymentAction('bankTransfer')}
                  buttonText="View Bank Details"
              />
              )}
            </div>
          </div>
        </div>

        {/* Pay After Section */}
        <div className="col-md-6">
          <div style={styles.paymentSectionBox}>
            <h2 style={styles.sectionTitle}>Pay After</h2>
            <p style={styles.sectionDescription}>Complete payment after your device is repaired.</p>
            <div style={styles.payAfterContent}>
              <p style={styles.payAfterText}>
                Once your booking is complete, you'll receive a shipping label via email to send your device to us.
              </p>
              <p style={styles.payAfterText}>
                After the repair is finished, we'll provide a detailed receipt and payment link.
              </p>
              <p style={styles.payAfterText}>
                Once payment is completed, your device will be securely returned to your doorstep.
              </p>
              <button
                onClick={() => handlePaymentSuccess(false)}
                style={styles.payAfterButton}
                className="btn btn-lg w-100 mt-3"
              >
                Book Now, Pay Later
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Section */}
      <footer className="text-center mt-5">
        <p className="small" style={styles.footerText}><FaLock size={12} style={styles.footerIcon} /> Secure payments processed by PCI-DSS compliant partners.</p>
        <p className="small" style={styles.footerText}>By proceeding, you agree to our{' '} <Link to="/terms" style={styles.footerLink}>Terms & Conditions</Link></p>
      </footer>

      {/* --- Modals --- */}

      {/* Stripe Elements / Wallet Modal Instance */}
      {/* Conditionally render the modal; ensure totalPrice is valid before rendering */}
      {modals.wallet && paymentData.totalPrice > 0 && (
          <UnifiedStripeModal
              key={paymentData.totalPrice} // Optional key to force re-render if price changes significantly
              show={modals.wallet}
              handleClose={() => {
                  console.log('Closing Wallet/Elements Modal.');
                  setModals(prev => ({ ...prev, wallet: false }));
                  setPaymentError(''); // Clear errors shown on main page when modal closes manually
              }}
              // Pass the main success handler function to be called by the modal
              onPaymentSuccess={() => handlePaymentSuccess(false)} // Indicate success is NOT from redirect
              totalPrice={paymentData.totalPrice}
          />
      )}

      {/* Success Modal Instance */}
       {modals.success && (
           <SuccessModal
                show={modals.success}
                handleClose={() => {
                    setModals(prev => ({ ...prev, success: false }));
                    console.log("Navigating to home after success.");
                    navigate('/'); // Navigate home after closing success modal
                }}
            />
       )}

      {/* Cancel Modal Instance */}
      {modals.cancel && (
          <CancelModal
                show={modals.cancel}
                handleClose={() => {
                    setModals(prev => ({ ...prev, cancel: false }));
                    console.log("Navigating back to booking after cancel.");
                    navigate('/booking'); // Navigate back to booking after closing cancel modal
                }}
            />
      )}

      {/* Bank Transfer Modal */}
      {modals.bankTransfer && settings.bankDetails && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ border: '2px solid #007BFF' }}>
              <div className="modal-header" style={{ backgroundColor: '#007BFF', color: '#fff' }}>
                <h5 className="modal-title"><FaUniversity className="me-2" />Bank Transfer Details</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setModals(prev => ({ ...prev, bankTransfer: false }))}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body" style={{ padding: '2rem' }}>
                <p style={{ color: '#6c757d', marginBottom: '1.5rem' }}>
                  Please transfer <strong>£{(paymentData.totalPrice || 0).toFixed(2)}</strong> to the following account:
                </p>
                <div style={{ backgroundColor: '#f8f9fa', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ color: '#007BFF' }}>Account Name:</strong>
                    <div style={{ fontSize: '1.1rem', color: '#6c757d', marginTop: '0.25rem' }}>{settings.bankDetails.accountName}</div>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ color: '#007BFF' }}>Sort Code:</strong>
                    <div style={{ fontSize: '1.1rem', color: '#6c757d', marginTop: '0.25rem' }}>{settings.bankDetails.sortCode}</div>
                  </div>
                  <div>
                    <strong style={{ color: '#007BFF' }}>Account Number:</strong>
                    <div style={{ fontSize: '1.1rem', color: '#6c757d', marginTop: '0.25rem' }}>{settings.bankDetails.accountNumber}</div>
                  </div>
                </div>
                <div style={{ backgroundColor: '#fff3cd', padding: '1rem', borderRadius: '8px', border: '1px solid #ffc107' }}>
                  <p style={{ margin: 0, color: '#6c757d', fontSize: '0.9rem' }}>
                    <strong>Important:</strong> Please include your name and booking reference in the payment description so we can match your payment.
                  </p>
                </div>
              </div>
              <div className="modal-footer" style={{ justifyContent: 'center' }}>
                <button
                  className="btn btn-primary"
                  style={{ backgroundColor: '#007BFF', border: 'none', padding: '0.75rem 2rem' }}
                  onClick={() => setModals(prev => ({ ...prev, bankTransfer: false }))}
                >
                  I've Made the Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
} // End of Payment Component


// --- Styles defined outside component ---
// Using a styles object for better organization
const styles = {
    headerTitle: { color: '#007BFF', fontWeight: 'bold' },
    headerSubtitle: { color: '#6c757d' },
    totalAmountStyle: { fontSize: '1.5rem', fontWeight: 'bold', color: '#007BFF', margin: '1rem 0' },
    errorBoxStyle: { padding: '1rem', margin: '1rem auto', border: '1px solid #dc3545', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', maxWidth: '600px', textAlign: 'left' },
    paymentSectionBox: {
        border: '2px solid #007BFF',
        borderRadius: '12px',
        padding: '2rem',
        backgroundColor: '#f8f9fa',
        height: '100%',
        boxShadow: '0 4px 8px rgba(0, 123, 255, 0.1)'
    },
    sectionTitle: {
        color: '#007BFF',
        fontSize: '1.8rem',
        fontWeight: 'bold',
        marginBottom: '1rem',
        textAlign: 'center'
    },
    sectionDescription: {
        color: '#6c757d',
        fontSize: '1rem',
        marginBottom: '1.5rem',
        textAlign: 'center'
    },
    payAfterContent: {
        padding: '1.5rem',
        backgroundColor: '#fff',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
    },
    payAfterText: {
        color: '#6c757d',
        fontSize: '0.95rem',
        lineHeight: '1.6',
        marginBottom: '1rem'
    },
    payAfterButton: {
        background: '#007BFF',
        color: '#fff',
        border: '2px solid #007BFF',
        borderRadius: '8px',
        padding: '0.75rem 1.5rem',
        fontSize: '1.1rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    paymentCardStyle: { border: '1px solid #007BFF', borderRadius: '8px', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', height: '100%', boxShadow: '0 2px 4px rgba(0, 123, 255, 0.1)', marginBottom: '1rem' },
    paymentCardBodyStyle: { textAlign: 'center', display: 'flex', flexDirection: 'column', padding: '1.5rem', flexGrow: 1 },
    paymentCardIconStyle: { color: '#007BFF', marginBottom: '1rem'},
    paymentCardTitleStyle: { color: '#007BFF', fontSize: '1rem', marginBottom: '0.5rem', flexGrow: 1 },
    paymentButtonStyleBase: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#007BFF', color: '#fff', border: '1px solid #007BFF',
        borderRadius: '4px', padding: '0.75rem 1.2rem',
        cursor: 'pointer', fontSize: '0.95rem',
        fontWeight: '500', textDecoration: 'none', width: '100%', marginTop: 'auto',
        transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out, border-color 0.2s ease-in-out',
        minHeight: '48px',
    },
    paymentButtonStyleHover: { background: '#0056b3', color: '#fff', borderColor: '#0056b3' },
    footerText: { color: '#6c757d' },
    footerIcon: { marginRight: '5px' },
    footerLink: { color: '#007BFF', textDecoration: 'underline' },
};

// --- Reusable Payment Option Component ---
// Using React.memo for potential performance optimization if props don't change often
const PaymentOption = React.memo(({ icon, title, action, buttonText, isLoading }) => {
    const [isHovered, setIsHovered] = useState(false);

    // Combine base style with hover style if isHovered is true and not loading
    const currentButtonStyle = {
        ...styles.paymentButtonStyleBase,
        ...(isHovered && !isLoading ? styles.paymentButtonStyleHover : {}),
        cursor: isLoading ? 'wait' : 'pointer',
        opacity: isLoading ? 0.7 : 1,
    };

    return (
        // Use Bootstrap grid column classes
        <div className="col-lg-4 col-md-6 d-flex"> {/* Use d-flex for equal height columns */}
            {/* Card container */}
            <div className="w-100" style={styles.paymentCardStyle}>
                {/* Flex column for card content */}
                <div style={styles.paymentCardBodyStyle}>
                    {/* Icon */}
                    <div style={styles.paymentCardIconStyle}>{icon}</div>
                    {/* Title */}
                    <h4 style={styles.paymentCardTitleStyle}>{title}</h4>
                    {/* Action Button */}
                    <button
                        style={currentButtonStyle}
                        onClick={!isLoading ? action : undefined} // Prevent multiple clicks when loading
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                             <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Processing...
                             </>
                         ) : (
                             // Determine button text, providing a sensible default
                             buttonText || `Proceed with ${title.split(' ')[0]}` // Simpler default
                         )}
                    </button>
                </div>
            </div>
        </div>
    );
});

export default Payment;